export class DateTools {
  public static calcTimeDifference(startDate: Date, endDate: Date): Date {
    const durationHours = Math.floor(
      Math.abs(startDate.getTime() - endDate.getTime()) / 3600000
    )
    const durationMinutes = Math.floor(
      (Math.abs(startDate.getTime() - endDate.getTime()) / 60000) % 60
    )
    const duration = new Date()
    duration.setHours(durationHours)
    duration.setMinutes(durationMinutes)
    duration.setSeconds(0)
    return duration
  }

  public static adjustForTimeZone(date: Date | null): Date | null {
    if (!date) return null

    const adjustedDate = new Date(date)
    adjustedDate.setHours(adjustedDate.getHours() + 1)
    return adjustedDate
  }

  public static formatDateForDisplay(
    start: Date | string | null,
    end: Date | string | null
  ): string {
    if (!start || !end) {
      return 'Date non spécifiée'
    }

    try {
      const startDate = typeof start === 'string' ? new Date(start) : start
      const endDate = typeof end === 'string' ? new Date(end) : end

      if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
        return 'Date invalide'
      }

      const options: Intl.DateTimeFormatOptions = {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }

      return `${startDate.toLocaleDateString('fr-FR', options)} - ${endDate.toLocaleDateString(
        'fr-FR',
        options
      )}`
    } catch (error) {
      console.error('Erreur lors du formatage de la plage de dates:', error)
      return 'Erreur de format de date'
    }
  }

  public static formatDateForAPI(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${date.getFullYear()}-${month}-${day}`
  }

  public static toISOString(date: Date | string | null): string | null {
    if (!date) return null

    const dateObj = typeof date === 'string' ? new Date(date) : date
    return dateObj.toISOString()
  }

  public static toLocalISOString(date: Date) {
    const tzOffset = date.getTimezoneOffset() * 60000 // offset in milliseconds
    return new Date(date.getTime() - tzOffset).toISOString().slice(0, 19)
  }

  public static formatTimeOnly(date: Date): string {
    if (!date) return ''

    const hours = date.getHours().toString().padStart(2, '0')
    const minutes = date.getMinutes().toString().padStart(2, '0')
    const seconds = date.getSeconds().toString().padStart(2, '0')

    return `${hours}:${minutes}:${seconds}`
  }

  /**
   * Vérifie si une date est valide
   */
  public static isValidDate(date: any): boolean {
    if (!date) return false
    const dateObj = date instanceof Date ? date : new Date(date)
    return !isNaN(dateObj.getTime())
  }

  /**
   * Convertit n'importe quelle entrée de date en objet Date valide
   * Retourne null si la conversion n'est pas possible
   */
  public static toValidDate(input: any): Date | null {
    if (!input) return null
    try {
      if (input instanceof Date) {
        return new Date(input)
      } else {
        const date = new Date(input)
        if (isNaN(date.getTime())) {
          return null
        }
        return date
      }
    } catch (e) {
      return null
    }
  }

  /**
   * Normalise une date en fonction de son type (début ou fin de journée)
   */
  public static normalizeDate(date: Date, isStartOfDay: boolean): Date {
    if (!date) return date
    const normalizedDate = new Date(date)

    if (isStartOfDay) {
      normalizedDate.setHours(0, 0, 0, 0)
    } else {
      normalizedDate.setHours(23, 59, 59, 999)
    }

    return normalizedDate
  }

  /**
   * Vérifie si une plage de dates est valide
   */
  public static isValidDateRange(dates: Date[]): boolean {
    if (!dates || dates.length !== 2) return false
    const [start, end] = dates
    if (!this.isValidDate(start) || !this.isValidDate(end)) return false
    return end >= start
  }

  /**
   * Calcule la durée en minutes entre deux dates
   */
  public static getDurationInMinutes(start: Date, end: Date): number {
    return Math.floor(Math.abs(end.getTime() - start.getTime()) / 60000)
  }

  /**
   * Crée un objet Date à partir d'une durée en minutes
   */
  public static createTimeFromDuration(durationMinutes: number): Date {
    const hours = Math.floor(durationMinutes / 60)
    const minutes = durationMinutes % 60

    const durationDate = new Date()
    durationDate.setHours(hours)
    durationDate.setMinutes(minutes)
    durationDate.setSeconds(0)

    return durationDate
  }

  /**
   * Génère un ID unique basé sur une liste d'IDs de produits
   */
  public static generateEventId(productIds: number[]): string {
    return `event_${Date.now()}_${productIds.sort().join('_')}`
  }

  /**
   * Crée une signature unique pour un ensemble de produits
   */
  public static createProductSignature(productIds: number[]): string {
    return productIds.sort().join('_')
  }

  /**
   * Vérifie si deux dates sont le même jour
   */
  public static isSameDay(date1: Date, date2: Date): boolean {
    if (!date1 || !date2) return false
    return (
      date1.getFullYear() === date2.getFullYear() &&
      date1.getMonth() === date2.getMonth() &&
      date1.getDate() === date2.getDate()
    )
  }

  /**
   * Formate une date au format yyyy-mm-dd
   */
  public static formatDateYMD(date: Date | null): string | undefined {
    if (!date) return undefined
    return `${date.getFullYear()}-${this.formatTwoDigits(date.getMonth() + 1)}-${this.formatTwoDigits(date.getDate())}`
  }

  /**
   * Formate un nombre avec deux chiffres (ajoute un 0 devant si nécessaire)
   */
  public static formatTwoDigits(n: number): string {
    return n < 10 ? '0' + n : String(n)
  }

  /**
   * Formate une date avec une heure pour l'API
   * @param date La date
   * @param time L'heure au format HH:mm:ss
   * @returns La chaîne formatée yyyy-mm-dd HH:mm:ss
   */
  public static formatDateTimeString(date: Date, time: string): string {
    const year = date.getFullYear()
    const month = this.formatTwoDigits(date.getMonth() + 1)
    const day = this.formatTwoDigits(date.getDate())
    return `${year}-${month}-${day} ${time}`
  }

  /**
   * Formate une durée en minutes en chaîne lisible
   * @param minutes La durée en minutes
   * @returns La chaîne formatée (ex: "2h 30min" ou "45min")
   */
  public static formatDuration(minutes: number): string {
    if (!minutes || minutes <= 0) return '0min'

    const hours = Math.floor(minutes / 60)
    const mins = Math.round(minutes % 60)

    if (hours === 0) return `${mins}min`
    if (mins === 0) return `${hours}h`
    return `${hours}h ${mins}min`
  }

  /**
   * Récupère la plage de dates de la semaine suivante (lundi-dimanche)
   * @returns Un tuple [lundi, dimanche] de la semaine suivante
   */
  public static getNextWeekRange(): [Date, Date] {
    const today = new Date()
    const nextMonday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate() + ((1 + 7 - today.getDay()) % 7)
    )
    const nextSunday = new Date(
      nextMonday.getFullYear(),
      nextMonday.getMonth(),
      nextMonday.getDate() + 6
    )
    return [nextMonday, nextSunday]
  }

  /**
   * Formate l'heure d'un événement pour l'affichage (start - end)
   * @param event L'événement calendrier
   * @returns La chaîne formatée (ex: "09:00 - 11:30")
   */
  public static formatEventTime(event: any): string {
    const startDate = new Date(event.start || event.startDate)
    const endDate =
      event.end || event.endDate ? new Date(event.end || event.endDate) : null

    const startTime = startDate.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })

    if (endDate) {
      const endTime = endDate.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      })
      return `${startTime} - ${endTime}`
    }

    return startTime
  }

  /**
   * Formate un créneau pré-sélectionné pour l'affichage
   * @param slot Le créneau avec start et end
   * @returns La chaîne formatée (ex: "14:00 - 16:00")
   */
  public static formatSlotTime(slot: { start: string; end?: string }): string {
    const startDate = new Date(slot.start)
    const endDate = slot.end ? new Date(slot.end) : null

    const startTime = startDate.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
    })

    if (endDate) {
      const endTime = endDate.toLocaleTimeString('fr-FR', {
        hour: '2-digit',
        minute: '2-digit',
      })
      return `${startTime} - ${endTime}`
    }

    return startTime
  }
}
